import { useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const REASONS = ["Fake", "Inaccurate", "Duplicate", "Other"];

/**
 * Small trigger + dialog so a broker can flag a counterpart listing
 * from a match card. Idempotent per (listing, reporter) on the server.
 */
export function ReportListingButton({ listingId }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("Inaccurate");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/reports/listing/${listingId}`, { reason, note: note.trim() });
      toast.success("Report submitted. Moderators will review.");
      setOpen(false);
      setNote("");
    } catch (err) {
      toast.error(formatApiError(err, "Report failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className="mb-focus-ring ml-auto inline-flex items-center rounded-full p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
        aria-label="Report listing"
        data-testid={`report-listing-trigger-${listingId}`}
      >
        <Flag className="h-3.5 w-3.5" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid={`report-listing-dialog-${listingId}`}>
          <DialogHeader>
            <DialogTitle>Report this listing</DialogTitle>
            <DialogDescription>
              Reports are sent to MatchBroker moderators. False reports are logged too.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-500">Reason</label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="report-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500">Note (optional)</label>
              <Textarea
                rows={3}
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="report-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="report-cancel">
              Cancel
            </Button>
            <Button
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={submit}
              disabled={busy}
              data-testid="report-submit"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
